import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { PRODUCT_CATEGORIES } from '../constants';
import { ArrowRight, Sparkles } from 'lucide-react';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

const ProductCard: React.FC<{ product: typeof PRODUCT_CATEGORIES[0]['products'][0], index: number }> = ({ product, index }) => (
    <RevealOnScroll delay={index * 50}>
        <Link to={product.path}>
            <GlassCard variant="medium" hover className="h-full group">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark group-hover:scale-110 transition-transform duration-300">
                        {product.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors">
                            {product.title}
                        </h3>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm mt-1">
                            {product.description}
                        </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-text-secondary-light dark:text-text-secondary-dark self-center transform transition-transform group-hover:translate-x-1 group-hover:text-primary-light dark:group-hover:text-primary-dark flex-shrink-0" />
                </div>
            </GlassCard>
        </Link>
    </RevealOnScroll>
);

export const ProduktyPage: React.FC = () => {
    usePageMetadata({
        title: "Wszystkie Narzędzia AI | BiznesBezKlikania.pl",
        description: "Przeglądaj wszystkie narzędzia AI. Odkryj rozwiązania dla obsługi klienta, marketingu i finansów. Przetestuj nasze dema i zautomatyzuj swój biznes.",
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[50vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark flex items-center justify-center">
                            <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Narzędziownia AI
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto">
                            Przeglądaj wszystkie nasze dema i gotowe rozwiązania. Znajdź narzędzia, które zautomatyzują Twój biznes.
                        </p>
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            {/* Products Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <div className="space-y-12">
                        {PRODUCT_CATEGORIES.map((category, catIndex) => (
                            <RevealOnScroll key={category.name} delay={catIndex * 100}>
                                <div>
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                                            {category.icon}
                                        </div>
                                        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                                            {category.name}
                                        </h2>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {category.products.map((product, prodIndex) => (
                                            <ProductCard key={product.path} product={product} index={prodIndex} />
                                        ))}
                                    </div>
                                </div>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection />
        </>
    );
};
